import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import { showCurrency } from '../../utils';
import { debtEstado, DEBT_ESTADO_TONE, DEBT_ESTADO_LABEL } from '../../utils/debtEstado';
import DebtDeletionModal from '../../components/DebtDeletionModal/DebtDeletionModal';
import {
	Section,
	StatStrip,
	DataTable,
	IdChip,
	Pill,
	ProgressBar,
	RowActionsMenu,
	Button,
	ExportModal,
	buildXlsxExport,
	exportAmount,
} from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AccountDebts({ accountId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();

	// Sends the editor back to this exact tab on save/cancel instead of letting
	// it land on the newly created debt's detail page.
	const returnTo = () => encodeURIComponent(location.pathname + location.search);
	const [deletingDebt, setDeletingDebt] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'id',
		accessors: { id: (debt) => debt.customId },
	});

	const stats = useMemo(() => {
		const activas = rows.filter((debt) => debtEstado(debt) === 'activa').length;
		const vencidas = rows.filter((debt) => debtEstado(debt) === 'vencida').length;
		const saldoByCurrency = new Map();
		rows.forEach((debt) => {
			const key = debt.currency || '—';
			saldoByCurrency.set(key, (saldoByCurrency.get(key) || 0) + (debt.saldo || 0));
		});
		return [
			{ label: 'Activas', value: activas },
			{
				label: 'Saldo total',
				value: [...saldoByCurrency.entries()].map(([currency, saldo]) => formatMoney(currency, saldo)).join(' · ') || '—',
				mono: true,
			},
			{ label: 'Vencidas', value: vencidas },
		];
	}, [rows]);

	const columns = [
		{ key: 'id', header: 'ID', sortable: true, render: (debt) => <IdChip>{debt.customId}</IdChip> },
		{ key: 'concept', header: 'Concepto', render: (debt) => debt.concept },
		{ key: 'date', header: 'Inicio', render: (debt) => formatDate(debt.date) },
		{ key: 'endDate', header: 'Fin', render: (debt) => formatDate(debt.endDate) },
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.amount)}</span>,
		},
		{
			key: 'cuotas',
			header: 'Cuotas',
			render: (debt) => (
				<div className='tw:min-w-[90px]'>
					<div className='tw:mb-1 tw:text-[12px] tw:tabular-nums'>
						{debt.cuotasPagas || 0}/{debt.cuotasTotal || 0}
					</div>
					<ProgressBar value={debt.cuotasPagas || 0} max={debt.cuotasTotal || 0} />
				</div>
			),
		},
		{
			key: 'saldo',
			header: 'Saldo',
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.saldo)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (debt) => {
				const estado = debtEstado(debt);
				return <Pill tone={DEBT_ESTADO_TONE[estado]}>{DEBT_ESTADO_LABEL[estado]}</Pill>;
			},
		},
	];

	const exportFields = [
		{ key: 'id', label: 'ID', locked: true },
		{ key: 'concept', label: 'Concepto' },
		{ key: 'date', label: 'Inicio' },
		{ key: 'endDate', label: 'Fin' },
		{ key: 'currency', label: 'Moneda' },
		{ key: 'amount', label: 'Monto' },
		{ key: 'cuotas', label: 'Cuotas' },
		{ key: 'saldo', label: 'Saldo' },
		{ key: 'estado', label: 'Estado' },
	];

	const exportGetters = {
		id: (debt) => debt.customId || '',
		concept: (debt) => debt.concept || '',
		date: (debt) => formatDate(debt.date),
		endDate: (debt) => formatDate(debt.endDate),
		currency: (debt) => debt.currency || '',
		amount: (debt) => exportAmount(debt.amount),
		cuotas: (debt) => `${debt.cuotasPagas || 0}/${debt.cuotasTotal || 0}`,
		saldo: (debt) => exportAmount(debt.saldo),
		estado: (debt) => DEBT_ESTADO_LABEL[debtEstado(debt)] || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Deudas',
			filePrefix: 'deudas',
		});

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => navigate(`/AdminScreen/accounts/account/${accountId}/debts/${debt._id}/edit`) },
				{ label: 'Eliminar', onClick: () => setDeletingDebt(debt), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button
					size='sm'
					variant='primary'
					onClick={() => navigate(`/AdminScreen/accounts/account/${accountId}/newDebt?returnTo=${returnTo()}`)}
				>
					Nueva deuda
				</Button>
			</div>
			<Section title='Deudas'>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(debt) => navigate(`/AdminScreen/debts/debt/${debt._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron deudas para esta cuenta.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle='Deudas de esta cuenta'
				onExport={handleExport}
			/>

			<DebtDeletionModal
				show={Boolean(deletingDebt)}
				debt={deletingDebt}
				onClose={() => setDeletingDebt(null)}
				onDeleted={() => {
					setDeletingDebt(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default AccountDebts;
